import OutlineTitle from "@/components/OutlineTitle";
import { useEffect, useState } from "react";
import { priceCompareAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, TrendingDown, TrendingUp, ExternalLink, Trash2 } from "lucide-react";
import { money } from "@/lib/constants";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Watchlist() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    priceCompareAPI
      .listSaved()
      .then((r) => setList(r.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const remove = async (id) => {
    try { await priceCompareAPI.deleteSaved(id); toast.success("Removed from watchlist"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not remove"); }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <p className="text-sm text-muted-foreground">Sign in to see your watched products.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <OutlineTitle>Your watchlist</OutlineTitle>
        <p className="mt-1 text-sm text-muted-foreground">Products you&apos;re tracking across retailers. We notify you when the price changes.</p>
      </div>

      {loading ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
      ) : list.length === 0 ? (
        <Card className="p-8 text-center">
          <Bell className="mx-auto h-8 w-8 text-primary/70" />
          <p className="mt-3 font-display text-lg font-bold uppercase">Nothing on watch yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Save products from Price Compare to track price changes.</p>
          <Link to="/price-compare">
            <Button className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90">Browse Price Compare</Button>
          </Link>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {list.map((s) => {
            const up = (s.price_delta || 0) > 0;
            return (
              <Card key={s.id} className="flex items-start gap-3 border-border bg-card p-4" data-testid={`watch-${s.id}`}>
                <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded bg-secondary">
                  {s.image_url && <img src={s.image_url} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold" title={s.title}>{s.title}</p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground/60">{s.retailer} · saved at {money(s.saved_price, s.currency)}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-display text-base font-bold text-primary">{money(s.last_price, s.currency)}</span>
                    {s.price_delta !== 0 && (
                      <span className={`flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-widest ${up ? "text-red-400" : "text-emerald-400"}`}>
                        {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {s.price_delta_pct > 0 ? "+" : ""}{s.price_delta_pct}%
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex gap-2">
                    {s.product_url && (
                      <a href={s.product_url} target="_blank" rel="noreferrer" data-testid={`watch-link-${s.id}`}>
                        <Button size="sm" variant="outline">
                          Visit <ExternalLink className="ml-1 h-3 w-3" />
                        </Button>
                      </a>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`watch-remove-${s.id}`}>
                      <Trash2 className="mr-1 h-3 w-3" /> Remove
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
